// Member questions in Vault Questions (founder 2026-11-03: "The notification
// panel alert will suffice for now"). A Club member's Daily Question waits
// here for review; the bell (backend utils/dailyQuestionReviewNotice, type
// member_question_review) opens the ones still waiting. Pure, for the tests.

import { formatEt } from "../User/introPauseUtils";

// The tier drop-down's values (VaultQuestionsListLayer), "" = All Tier.
export const TIER_VALUES = ["", "iceBreaker", "deeperDive", "wildCard", "dailyQuestion", "dailyQuestion:asked", "keptTogether", "gatekeeper"];
const REVIEW_STATUS_VALUES = ["approved", "pending", "rejected"];

/**
 * The list's filters from the address (?status, ?isPublished, and since
 * 2026-11-03 ?tier and ?reviewStatus). Same as before for status and
 * isPublished. tier: only a value the drop-down has ("all" = All Tier);
 * left out, the tier the admin picked stays. reviewStatus: a human decline
 * keeps status pending_review (backend humanReviewUpdate sets only
 * reviewStatus), so "waiting for review" also needs reviewStatus=pending.
 * A link to an address with any filter (the bell, a Dashboard card) starts
 * on page 1, so it never lands on an empty later page. Back or Forward
 * (navigationType "POP", react-router useNavigationType) keeps the page the
 * admin was on: back from a question's View page lands where they were, as
 * before 2026-11-03 (review 2026-11-03).
 */
export const listFiltersFromSearch = (search, { navigationType } = {}) => {
  const params = new URLSearchParams(search || "");
  let statusFilter = params.get("status") || "all";
  // The pending filter from the address includes both keys.
  if (statusFilter === "pending_ai_review") statusFilter = "pending_ai_review,pending_review";
  const reviewStatus = params.get("reviewStatus");
  const filters = {
    statusFilter,
    isPublishedFilter: params.get("isPublished") || "all",
    reviewStatusFilter: REVIEW_STATUS_VALUES.includes(reviewStatus) ? reviewStatus : "all",
  };
  if (params.has("tier")) {
    const tier = params.get("tier") === "all" ? "" : params.get("tier");
    if (TIER_VALUES.includes(tier)) filters.tierFilter = tier;
  }
  if (navigationType !== "POP" && ["status", "isPublished", "tier", "reviewStatus"].some((k) => params.has(k))) filters.currentPage = 1;
  return filters;
};

/**
 * The Status drop-down's value for the filters in the store. Its "Pending
 * AI Review" sends both pending keys (pending_ai_review,pending_review), and
 * the Dashboard's Pending Review card sends pending_review alone: all three
 * read "Pending AI Review". (Before 2026-11-03 the two keys together, which
 * the drop-down itself sets, read "All Vault".)
 */
export const statusDropdownValue = ({ statusFilter, isPublishedFilter } = {}) => {
  if (isPublishedFilter === "true") return "published";
  if (statusFilter === "ai_approved") return "ai_approved";
  if (["pending_ai_review", "pending_review", "pending_ai_review,pending_review"].includes(statusFilter)) return "pending_ai_review";
  if (statusFilter === "ai_rejected") return "ai_rejected";
  return "all";
};

// The bell row for a member question waiting for review.
export const MEMBER_QUESTION_ALERT_TYPE = "member_question_review";
// Daily Questions, member submitted, not reviewed yet (a declined one is
// out). status=pending_ai_review is both pending keys, what the Status
// drop-down's "Pending AI Review" means everywhere else, so a switch of Tier
// after the bell keeps meaning what the drop-down says; a member question is
// always pending_review until reviewed (backend utils/dailyQuestionAsk), so
// the bell's list is the same rows.
export const MEMBER_QUESTIONS_WAITING_PATH = "/vault-questions?tier=dailyQuestion:asked&status=pending_ai_review&reviewStatus=pending";
// A test account's question is not in "member submitted" (backend
// tierFilterFor), so a bell row for test questions only opens every tier,
// where it shows tagged "test account question".
export const TEST_QUESTIONS_WAITING_PATH = "/vault-questions?tier=all&status=pending_ai_review&reviewStatus=pending";

/** Where a click on the bell's member question row goes (the whole group). */
export const memberQuestionBellPath = (n) => {
  const rows = [n, ...(Array.isArray(n?.subNotifications) ? n.subNotifications : [])].filter(Boolean);
  const onlyTest = rows.length > 0 && rows.every((r) => String(r?.title || "").startsWith("[Test]"));
  return onlyTest ? TEST_QUESTIONS_WAITING_PATH : MEMBER_QUESTIONS_WAITING_PATH;
};

// WHO ASKED IT (2026-11-03). The backend's list and view now send, on a
// member-asked row only: askedByFirstName (or null), askedByIdTail (last 6
// of their id), askedPocketName (or null), askedAt, and waitingDays (whole
// days since it was asked while reviewStatus is pending, else null). Never
// the email. A server without them still draws the id tail and the time.

const MEMBER_ID = /^[a-f0-9]{24}$/;

/** "Nicole (..1b143b)", or "A member (..1b143b)" with no usable name. */
export const askerName = (item) => {
  if (!item || !item.askedBy) return "";
  const tail = item.askedByIdTail || String(item.askedBy).slice(-6);
  return `${item.askedByFirstName || "A member"} (..${tail})`;
};

// A DELETED ASKER (review 2026-11-03). Deleting an account keeps its name,
// and the member's Admin page answers 404 for it (backend user_details),
// which View User draws as a blank page. The backend marks such a row
// askedByDeleted: true (withAskerFields; until it does, nothing changes
// here): the name is then plain text, no link, with ", deleted account".

/** The member's Admin page (the route the bell uses), or null. */
export const askerPagePath = (item) => {
  const id = item && item.askedBy ? String(item.askedBy) : "";
  if (item && item.askedByDeleted === true) return null;
  return MEMBER_ID.test(id) ? `/user/view-user/${id}` : null;
};

/** "Nicole (..1b143b)", or "Nicole (..1b143b), deleted account". */
export const askerLabel = (item) => {
  const name = askerName(item);
  return name && item.askedByDeleted === true ? `${name}, deleted account` : name;
};

// 🔴 THE NOTE A MEMBER QUESTION WAS SAVED WITH IS NOT TRUE (review
// 2026-11-03). The backend (utils/dailyQuestionAsk) stores "Asked by a member
// (Ask the Daily Question). Publish to run it; the card never shows a name."
// on every real member's question, Nicole's included. Publish does not run
// it: the Publish button is off while it waits, the publish endpoint only
// sets isPublished, and the drop picks by isActive, which the approval (green
// check) or an AI approval sets. So that note is never shown; any other note
// (a test account's, or one an admin wrote) shows as written.
const UNTRUE_MEMBER_NOTE = /Publish to run it/i;

/** The question's internal note to show, or "". */
export const internalNoteLine = (item) => {
  const note = String((item && item.notesInternal) || "").trim();
  return note && !UNTRUE_MEMBER_NOTE.test(note) ? note : "";
};

/** "Asked Oct 18, 11:11 PM ET", or "" with no time. */
export const askedAtLine = (item) => {
  const when = formatEt(item && item.askedAt);
  return when ? `Asked ${when}` : "";
};

/**
 * While it waits for review: { text: "Waiting 16 days", late }, late (red)
 * from 2 days. null once reviewed, or from a server that does not say.
 */
export const waitingLine = (item) => {
  const days = item ? item.waitingDays : null;
  if (typeof days !== "number" || !Number.isFinite(days) || days < 0) return null;
  const text = days < 1 ? "Waiting less than a day" : days === 1 ? "Waiting 1 day" : `Waiting ${days} days`;
  return { text, late: days >= 2 };
};

// THE REVIEW BOXES (2026-11-03). A declined member question shows the reason
// on the member's You tab (backend utils/dailyQuestionAsk mine: "not this
// time" with the reviewNote of a human decline), so the box says so; a bank
// question has nobody to see it.
export const declineReasonPlaceholder = (item) =>
  item && item.askedBy ? "Reason (optional). The member sees this reason on their You tab." : "Reason (optional, kept with the question)";

// A member question's approve reply (backend approveReplyMessage) says which
// evening it runs and whether the member was told, with askerNotice as data.
// It is shown in a dialog the admin closes: the banner is gone in a second.
export const isMemberApprovalReply = (data) => !!(data && data.status === 1 && data.askerNotice && data.message);

// ---------------------------------------------------------------------------
// SHOWN ONCE: "COMPLETE" (founder 2026-11-04, "Yes to all 6, build them").
// Nicole's "Loud music after work?" was Ottawa's Daily Question on Tuesday
// 11-03 and still read "Active" here the next morning, because running a
// question never writes to its own row. A member question is shown to its
// city once, so from 8 PM ET on that evening it reads Complete: kept for
// history, never deleted for this, its question, answers, tier and type
// locked (tags still change). Nothing is stored: the backend works it out
// from the day rows (admin.vaultQuestionController withAskerFields, the same
// rule as the asker's You tab) and sends, on a member row only:
//   shownState   "complete" (shown), "tonight" (dealt at 7 PM, shown at the
//                drop) or null (not shown yet)
//   shownOn      the evening, "2026-11-03" (a calendar day in Eastern time)
//   shownCity    that evening's city, or null
//   answersOpen  true while that evening is the live one
//   dropHour     20 (the "8 PM" the words name)
// The five are left out when the server could not read the day rows (or is
// older than this): nobody knows then, so the row draws today's pill.
// A bank question (askedBy null) rotates and is never Complete.
// ---------------------------------------------------------------------------

const ET_ZONE = "America/Toronto";

// A "YYYY-MM-DD" key is a calendar day, never a moment: new Date(key) reads
// as UTC midnight, the evening before in Eastern time. Noon UTC, read in UTC,
// is that day everywhere.
const dayOf = (key) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ""));
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12)) : null;
};
const dayWords = (key, opts) => {
  const d = dayOf(key);
  return d ? d.toLocaleDateString("en-US", { ...opts, timeZone: "UTC" }) : "";
};

/** "Tuesday, Nov 3" for "2026-11-03", or "". */
export const eveningLong = (key) => {
  const d = dayOf(key);
  return d ? `${dayWords(key, { weekday: "long" })}, ${dayWords(key, { month: "short", day: "numeric" })}` : "";
};
/** "Tue, Nov 3", or "". */
export const eveningShort = (key) => {
  const d = dayOf(key);
  return d ? `${dayWords(key, { weekday: "short" })}, ${dayWords(key, { month: "short", day: "numeric" })}` : "";
};
/** "Wed", or "". */
export const weekdayShort = (key) => dayWords(key, { weekday: "short" });
/** The calendar day after a key ("2026-11-04" for "2026-11-03"), or "". */
export const dayAfter = (key) => {
  const d = dayOf(key);
  if (!d) return "";
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};
/** "8 PM" for the drop hour 20 (8 PM when the server did not say). */
export const hourWords = (hour) => {
  const h = Number.isInteger(hour) && hour >= 0 && hour < 24 ? hour : 20;
  return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;
};
/** Today's calendar day in Eastern time, "2026-11-04". */
export const etDayKey = (now = new Date()) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: ET_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);

/** A member question already shown to its city. */
export const isComplete = (item) => !!(item && item.askedBy && item.shownState === "complete");
/** A member question dealt at 7 PM and shown at the drop. Still Active. */
export const isDealtNotShown = (item) => !!(item && item.askedBy && item.shownState === "tonight");

const inCity = (item) => (item && item.shownCity ? `in ${item.shownCity} ` : "");

/**
 * The list's line under the Complete pill: "Shown in Ottawa Tue, Nov 3 ·
 * answers open until 8 PM ET Wed" while that evening is the live one, then
 * "Shown in Ottawa Tue, Nov 3".
 */
export const completeLine = (item) => {
  if (!isComplete(item)) return "";
  const shown = `Shown ${inCity(item)}${eveningShort(item.shownOn)}`;
  return item.answersOpen === true
    ? `${shown} · answers open until ${hourWords(item.dropHour)} ET ${weekdayShort(dayAfter(item.shownOn))}`
    : shown;
};

/** The Complete pill's tooltip. */
export const completeTooltip = (item) => {
  if (!isComplete(item)) return "";
  const whose = item.shownCity ? `${item.shownCity}'s` : "its city's";
  return `A member question is shown once. This one was ${whose} Daily Question on ${eveningLong(item.shownOn)}. It will not be shown again.`;
};

/** The View page's line under the pill: "Shown in Ottawa on Tuesday, Nov 3 at 8 PM ET". */
export const completeStatusLine = (item) =>
  isComplete(item) ? `Shown ${inCity(item)}on ${eveningLong(item.shownOn)} at ${hourWords(item.dropHour)} ET` : "";

/**
 * Under the Active pill between the 7 PM deal and the 8 PM drop: "Shows
 * tonight in Ottawa at 8 PM ET". A day dealt for a later evening (only the
 * lab does that) names its evening instead, so the line is never untrue.
 */
export const dealtLine = (item, now = new Date()) => {
  if (!isDealtNotShown(item)) return "";
  const where = item.shownCity ? ` in ${item.shownCity}` : "";
  const hour = hourWords(item.dropHour);
  return item.shownOn === etDayKey(now)
    ? `Shows tonight${where} at ${hour} ET`
    : `Shows${where} on ${eveningLong(item.shownOn)} at ${hour} ET`;
};

/**
 * The Edit page's banner on a Complete question. The backend refuses a new
 * question, answers, tier or type for it (vault_question_update, 409): its
 * numbers are stored by answer position, so new words would carry the
 * city's answers, and a tier change could let the Vault serve it.
 */
export const lockedBanner = (item) =>
  isComplete(item)
    ? `Shown ${inCity(item)}on ${eveningLong(item.shownOn)}. The question, answers, tier and type are locked so its Stats stay true. Tags can still change.`
    : "";

/**
 * The delete box. A Complete question also leaves the asker's You tab
 * (backend utils/dailyQuestionAsk mine reads only rows not deleted), and it
 * never needs deleting: it is never shown again.
 */
export const deleteConfirmText = (item) => {
  if (!isComplete(item)) return "This action will delete the Vault Question!";
  if (item.askedByDeleted === true) return "Delete this question? You can keep it for its history: a Complete question is never shown again.";
  const whose = item.askedByFirstName ? `${item.askedByFirstName}'s` : "the asker's";
  const city = item.shownCity || "its city";
  return `Delete this question? It also comes off ${whose} You tab, with how ${city} answered. You can keep it: a Complete question is never shown again.`;
};

/**
 * The list's "Delete all" box (review 2026-11-04): the checkbox delete takes
 * a Complete question off its asker's You tab too, so it says so, as the
 * row's own bin does. rows: the selected rows the page has loaded; count:
 * how many are selected. No Complete row: today's words.
 */
export const bulkDeleteConfirmText = (rows, count) => {
  const list = (rows || []).filter(Boolean);
  const n = Number.isInteger(count) && count > 0 ? count : list.length;
  const done = list.filter(isComplete);
  if (!done.length) return `This action will delete the Vault Question${n > 1 ? "s" : ""}!`;
  if (n === 1) return deleteConfirmText(done[0]);
  const k = done.length;
  const lead = k === 1 ? "One is a Complete member question." : `${k === n ? (n === 2 ? "Both" : `All ${n}`) : k} are Complete member questions.`;
  const live = done.filter((r) => r.askedByDeleted !== true);
  if (!live.length) {
    return `Delete these ${n} questions? ${lead} You can keep ${k === 1 ? "it for its" : "them for their"} history: a Complete question is never shown again.`;
  }
  const askers = new Set(live.map((r) => String(r.askedBy)));
  const cities = new Set(live.map((r) => r.shownCity || ""));
  const whose =
    askers.size === 1 ? (live[0].askedByFirstName ? `${live[0].askedByFirstName}'s You tab` : "the asker's You tab") : "their askers' You tabs";
  const city = cities.size === 1 && live[0].shownCity ? live[0].shownCity : live.length === 1 ? "its city" : "their cities";
  const who = live.length === k ? (k === 1 ? "It also comes" : "They also come") : live.length === 1 ? "One of them also comes" : `${live.length} of them also come`;
  return `Delete these ${n} questions? ${lead} ${who} off ${whose}, with how ${city} answered. You can keep ${k === 1 ? "it" : "them"}: a Complete question is never shown again.`;
};

/**
 * Bulk Edit's words when the server refuses the save over a Complete row
 * (review 2026-11-04; backend vault_question_bulk_upsert, 409 with code
 * "complete", id and questionText): which row, and that the whole save was
 * refused before any write, so the other rows did not save either. null for
 * any other answer.
 */
export const bulkCompleteWords = (payload) => {
  if (!payload || typeof payload !== "object" || payload.code !== "complete") return null;
  const said = String(payload.message || payload.error || "").trim();
  const q = String(payload.questionText || "").trim();
  return `${q ? `"${q}": ` : ""}${said ? `${said} ` : ""}Nothing in this save was kept.`;
};

/**
 * The Add and Edit pages' error line (review 2026-11-04). The thunks reject
 * with a string: the server's own words on a refusal it explains
 * (refusalWords below, such as a Complete question's 409), or axios's
 * technical message, which the page never showed and still does not.
 */
const AXIOS_WORDS = /^(Request failed with status code \d+|Network Error|timeout of \d+ms exceeded|canceled)$/;
export const saveErrorWords = (error) => {
  const s = typeof error === "string" ? error.trim() : "";
  return s && !AXIOS_WORDS.test(s) ? s : "Something went wrong";
};

// ---------------------------------------------------------------------------
// IN QUESTION INFO (founder 2026-11-04): labelled "Member question" rows at
// the top of the View page's Question Info. The backend adds, on a member
// row: askedByClub (true while their Club is live now, null when unknown),
// askedApprovedAt (approvedAt, else publishedAt, while approved; null when
// no time is known), askedApprovedBy ("person" or "ai"), and on the view
// only answeredCount (a number once Complete, else null).
// ---------------------------------------------------------------------------

/** "Approved:" on the View page. */
export const approvedWords = (item) => {
  if (!item || !item.askedBy) return "";
  if (item.reviewStatus === "approved") {
    // A server older than these fields says only that it is approved.
    if (item.askedApprovedAt === undefined) return "Yes";
    const when = formatEt(item.askedApprovedAt);
    if (!when) return "Yes (time not recorded)";
    return item.askedApprovedBy === "ai" ? `${when}, by the AI review` : when;
  }
  if (item.reviewStatus === "rejected" || item.status === "ai_rejected") {
    const when = formatEt(item.declinedByPersonAt);
    if (when) return `Declined ${when}`;
    return item.status === "ai_rejected" ? "Declined by the AI review" : "Declined";
  }
  return "Not yet";
};

/**
 * "Shown to the city:" on the View page, or null when the server could not
 * say (the row is left out then).
 */
export const shownToCityWords = (item, now = new Date()) => {
  if (!item || !item.askedBy || !("shownState" in item)) return null;
  const hour = hourWords(item.dropHour);
  if (item.shownState === "complete") return `${eveningLong(item.shownOn)}, ${hour} ET`;
  if (item.shownState === "tonight") {
    return item.shownOn === etDayKey(now) ? `Tonight, ${hour} ET` : `${eveningLong(item.shownOn)}, ${hour} ET, not shown yet`;
  }
  if (item.reviewStatus === "rejected" || item.status === "ai_rejected") return "Not shown (declined)";
  return "Not yet";
};

/**
 * "Answered:" on the View page, once Complete: "34 so far" while answers are
 * open, then "34". "" when the count could not be read (the Stats link still
 * shows); null before it was shown (the row is left out).
 */
export const answeredWords = (item) => {
  if (!isComplete(item)) return null;
  const n = item.answeredCount;
  if (typeof n !== "number" || !Number.isFinite(n)) return "";
  return item.answersOpen === true ? `${n} so far` : `${n}`;
};

/**
 * The words a refusal carries: the server's own, on a 4xx it explains (the
 * Complete refusals are 409 with code "complete"). A server error or no
 * answer keeps the caller's words.
 */
export const refusalWords = (err) => {
  const status = err && err.response && err.response.status;
  const data = err && err.response && err.response.data;
  if (!status || status >= 500 || status === 401 || !data || typeof data !== "object") return "";
  return String(data.message || data.error || "");
};
